/**
 * Currency formatting and conversion utilities
 * Maps country codes to currency codes and locales for proper formatting
 * Handles currency conversion from USD to local currencies
 */

// Map of country codes to currency information
const COUNTRY_CURRENCY_MAP = {
  // Major markets
  US: { currency: "USD", locale: "en-US" },
  CA: { currency: "CAD", locale: "en-CA" },
  GB: { currency: "GBP", locale: "en-GB" },
  AU: { currency: "AUD", locale: "en-AU" },
  NZ: { currency: "NZD", locale: "en-NZ" },
  IE: { currency: "EUR", locale: "en-IE" },

  // Europe
  DE: { currency: "EUR", locale: "de-DE" },
  FR: { currency: "EUR", locale: "fr-FR" },
  ES: { currency: "EUR", locale: "es-ES" },
  IT: { currency: "EUR", locale: "it-IT" },
  NL: { currency: "EUR", locale: "nl-NL" },
  BE: { currency: "EUR", locale: "nl-BE" },
  AT: { currency: "EUR", locale: "de-AT" },
  CH: { currency: "CHF", locale: "de-CH" },
  SE: { currency: "SEK", locale: "sv-SE" },
  NO: { currency: "NOK", locale: "nb-NO" },
  DK: { currency: "DKK", locale: "da-DK" },
  FI: { currency: "EUR", locale: "fi-FI" },
  PL: { currency: "PLN", locale: "pl-PL" },
  CZ: { currency: "CZK", locale: "cs-CZ" },
  HU: { currency: "HUF", locale: "hu-HU" },
  RO: { currency: "RON", locale: "ro-RO" },
  BG: { currency: "BGN", locale: "bg-BG" },
  HR: { currency: "EUR", locale: "hr-HR" },
  SI: { currency: "EUR", locale: "sl-SI" },
  SK: { currency: "EUR", locale: "sk-SK" },
  EE: { currency: "EUR", locale: "et-EE" },
  LV: { currency: "EUR", locale: "lv-LV" },
  LT: { currency: "EUR", locale: "lt-LT" },

  // Asia Pacific
  IN: { currency: "INR", locale: "en-IN" },
  JP: { currency: "JPY", locale: "ja-JP" },
  KR: { currency: "KRW", locale: "ko-KR" },
  CN: { currency: "CNY", locale: "zh-CN" },
  HK: { currency: "HKD", locale: "zh-HK" },
  SG: { currency: "SGD", locale: "en-SG" },
  MY: { currency: "MYR", locale: "ms-MY" },
  TH: { currency: "THB", locale: "th-TH" },
  ID: { currency: "IDR", locale: "id-ID" },
  PH: { currency: "PHP", locale: "en-PH" },
  VN: { currency: "VND", locale: "vi-VN" },
  TW: { currency: "TWD", locale: "zh-TW" },

  // Middle East & Africa
  AE: { currency: "AED", locale: "ar-AE" },
  SA: { currency: "SAR", locale: "ar-SA" },
  IL: { currency: "ILS", locale: "he-IL" },
  ZA: { currency: "ZAR", locale: "en-ZA" },
  EG: { currency: "EGP", locale: "ar-EG" },
  TR: { currency: "TRY", locale: "tr-TR" },

  // South America
  BR: { currency: "BRL", locale: "pt-BR" },
  MX: { currency: "MXN", locale: "es-MX" },
  AR: { currency: "ARS", locale: "es-AR" },
  CL: { currency: "CLP", locale: "es-CL" },
  CO: { currency: "COP", locale: "es-CO" },
  PE: { currency: "PEN", locale: "es-PE" },
  UY: { currency: "UYU", locale: "es-UY" },

  // Others
  RU: { currency: "RUB", locale: "ru-RU" },
  UA: { currency: "UAH", locale: "uk-UA" },
};

// Default to USD for unknown countries
const DEFAULT_CURRENCY = { currency: "USD", locale: "en-US" };

// Exchange rate cache to avoid repeated API calls
let exchangeRatesCache = {
  rates: {},
  lastUpdated: null,
  baseCurrency: "USD",
};

// Cache duration in milliseconds (1 hour)
const CACHE_DURATION = 60 * 60 * 1000;

/**
 * Get currency information for a given country code
 * @param {string} countryCode - Two-letter country code (e.g., 'US', 'IN', 'GB')
 * @returns {Object} Currency information with currency code and locale
 */
export function getCurrencyInfo(countryCode) {
  if (!countryCode || typeof countryCode !== "string") {
    return DEFAULT_CURRENCY;
  }

  const upperCountryCode = countryCode.toUpperCase();
  return COUNTRY_CURRENCY_MAP[upperCountryCode] || DEFAULT_CURRENCY;
}

/**
 * Format a number as currency based on the country code
 * @param {number} amount - The amount to format
 * @param {string} countryCode - Two-letter country code (e.g., 'US', 'IN', 'GB')
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, countryCode, options = {}) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "N/A";
  }

  const currencyInfo = getCurrencyInfo(countryCode);

  try {
    return new Intl.NumberFormat(currencyInfo.locale, {
      style: "currency",
      currency: currencyInfo.currency,
      minimumFractionDigits: options.minimumFractionDigits ?? 2,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      ...options,
    }).format(Number(amount));
  } catch (error) {
    console.warn("Error formatting currency:", error);
    // Fallback to USD formatting
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  }
}

/**
 * Get the currency symbol for a country code
 * @param {string} countryCode - Two-letter country code
 * @returns {string} Currency symbol (e.g., '$', '€', '¥')
 */
export function getCurrencySymbol(countryCode) {
  const currencyInfo = getCurrencyInfo(countryCode);

  try {
    const formatter = new Intl.NumberFormat(currencyInfo.locale, {
      style: "currency",
      currency: currencyInfo.currency,
    });

    // Extract symbol from formatted zero
    const formatted = formatter.formatToParts(0);
    const symbolPart = formatted.find((part) => part.type === "currency");
    return symbolPart ? symbolPart.value : "$";
  } catch (error) {
    console.warn("Error getting currency symbol:", error);
    return "$";
  }
}

/**
 * Format currency with compact notation for large numbers
 * @param {number} amount - The amount to format
 * @param {string} countryCode - Two-letter country code
 * @returns {string} Formatted currency string with compact notation (e.g., '$1.2K', '€50M')
 */
export function formatCurrencyCompact(amount, countryCode) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "N/A";
  }

  const currencyInfo = getCurrencyInfo(countryCode);

  try {
    return new Intl.NumberFormat(currencyInfo.locale, {
      style: "currency",
      currency: currencyInfo.currency,
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 1,
    }).format(Number(amount));
  } catch (error) {
    console.warn("Error formatting compact currency:", error);
    // Fallback to regular formatting
    return formatCurrency(amount, countryCode);
  }
}

/**
 * Fetch exchange rates from a reliable API
 * @param {string} baseCurrency - Base currency code (default: USD)
 * @returns {Promise<Object|null>} Exchange rates object or null on error
 */
async function fetchExchangeRates(baseCurrency = "USD") {
  try {
    // Using exchangerate-api.com (free tier: 1500 requests/month)
    const response = await fetch(
      `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      rates: data.rates,
      baseCurrency: data.base,
      lastUpdated: new Date(),
    };
  } catch (error) {
    console.warn("Failed to fetch exchange rates from primary API:", error);

    // Fallback to fixer.io (requires API key, but has a free tier)
    // You can add your API key here or use another fallback service
    try {
      // Alternative: use a free service without API key
      const fallbackResponse = await fetch(
        `https://open.er-api.com/v6/latest/${baseCurrency}`
      );

      if (!fallbackResponse.ok) {
        throw new Error(
          `Fallback API error! status: ${fallbackResponse.status}`
        );
      }

      const fallbackData = await fallbackResponse.json();
      return {
        rates: fallbackData.rates,
        baseCurrency: fallbackData.base_code,
        lastUpdated: new Date(),
      };
    } catch (fallbackError) {
      console.error("All exchange rate APIs failed:", fallbackError);
      return null;
    }
  }
}

/**
 * Get cached or fresh exchange rates
 * @param {string} baseCurrency - Base currency code
 * @returns {Promise<Object|null>} Exchange rates or null
 */
async function getExchangeRates(baseCurrency = "USD") {
  const now = new Date();

  // Check if we have fresh cached data
  if (
    exchangeRatesCache.rates &&
    exchangeRatesCache.lastUpdated &&
    exchangeRatesCache.baseCurrency === baseCurrency &&
    now - exchangeRatesCache.lastUpdated < CACHE_DURATION
  ) {
    return exchangeRatesCache;
  }

  // Fetch fresh rates
  const freshRates = await fetchExchangeRates(baseCurrency);

  if (freshRates) {
    exchangeRatesCache = freshRates;
    return freshRates;
  }

  // If fetch failed but we have cached data (even if stale), use it
  if (
    exchangeRatesCache.rates &&
    exchangeRatesCache.baseCurrency === baseCurrency
  ) {
    console.warn("Using stale exchange rate data due to API failure");
    return exchangeRatesCache;
  }

  return null;
}

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {Promise<number|null>} Converted amount or null on error
 */
async function convertCurrency(
  amount,
  fromCurrency = "USD",
  toCurrency = "USD"
) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return null;
  }

  // No conversion needed for same currency
  if (fromCurrency === toCurrency) {
    return Number(amount);
  }

  try {
    const ratesData = await getExchangeRates(fromCurrency);

    if (!ratesData || !ratesData.rates) {
      console.warn(
        `Failed to get exchange rates for ${fromCurrency} to ${toCurrency}`
      );
      return Number(amount); // Return original amount if conversion fails
    }

    const exchangeRate = ratesData.rates[toCurrency];

    if (!exchangeRate) {
      console.warn(`Exchange rate not found for ${toCurrency}`);
      return Number(amount);
    }

    return Number(amount) * exchangeRate;
  } catch (error) {
    console.error("Currency conversion error:", error);
    return Number(amount); // Return original amount on error
  }
}

/**
 * Format currency with conversion from USD to local currency
 * @param {number} usdAmount - Amount in USD
 * @param {string} countryCode - Two-letter country code
 * @param {Object} options - Additional formatting options
 * @returns {Promise<string>} Formatted currency string with converted amount
 */
export async function formatCurrencyWithConversion(
  usdAmount,
  countryCode,
  options = {}
) {
  if (usdAmount === null || usdAmount === undefined || isNaN(usdAmount)) {
    return "N/A";
  }

  const currencyInfo = getCurrencyInfo(countryCode);
  const targetCurrency = currencyInfo.currency;

  // Convert from USD to target currency
  const convertedAmount = await convertCurrency(
    usdAmount,
    "USD",
    targetCurrency
  );

  if (convertedAmount === null) {
    // Fallback to USD formatting if conversion fails
    return formatCurrency(usdAmount, "US", options);
  }

  return formatCurrency(convertedAmount, countryCode, options);
}

/**
 * Get current exchange rate for a currency pair
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @returns {Promise<number|null>} Exchange rate or null
 */
export async function getExchangeRate(
  fromCurrency = "USD",
  toCurrency = "USD"
) {
  if (fromCurrency === toCurrency) {
    return 1;
  }

  try {
    const ratesData = await getExchangeRates(fromCurrency);
    return ratesData?.rates?.[toCurrency] || null;
  } catch (error) {
    console.error("Error getting exchange rate:", error);
    return null;
  }
}

export default {
  getCurrencyInfo,
  formatCurrency,
  getCurrencySymbol,
  formatCurrencyCompact,
  formatCurrencyWithConversion,
  convertCurrency,
  getExchangeRate,
};
